import { useState, useEffect, useRef, forwardRef, lazy, Suspense } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, Home, BookOpen, Star, Heart, Share2, Sparkles, Eye, Pencil, Check, X, Volume2, VolumeX, Pause, Play, RefreshCw, Loader2, Image, MoreVertical, Crown, Maximize2, Minimize2, Info, Wand2, Calendar, Palette, Users, Download, RotateCcw, Layers, Printer } from "lucide-react";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import VersionBadge from "@/components/VersionBadge";
import ClickableAuthor from "@/components/ClickableAuthor";
import VideoPlayer from "@/components/VideoPlayer";
import StoryboardEditor from "@/components/StoryboardEditor";
import ThumbnailPicker from "@/components/ThumbnailPicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import { CATEGORY_LABELS } from "@/constants/categories";
import { getBookTypeLabel } from "@/lib/utils";

// Lazy load 3D book to avoid loading Three.js on mobile
const Book3D = lazy(() => import("@/components/Book3D"));

// Check if device supports WebGL for 3D book
const hasWebGLSupport = () => {
  if (typeof window === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
};

// Check if device is tablet or larger (not mobile phone)
const isTabletOrLarger = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 768;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper to get full audio URL (handles both local /static/ paths and CDN URLs)
const getAudioUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper to get full video URL (handles both local /static/ paths and CDN URLs)
const getVideoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// ============ Fullscreen Hook ============
// Uses CSS-based fullscreen for consistent experience across all devices
// This hides the sidebar and makes the story take over the full viewport
const useFullscreen = (elementRef) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const enterFullscreen = () => {
    setIsFullscreen(true);
    // Prevent body scroll when in fullscreen
    document.body.style.overflow = 'hidden';
  };

  const exitFullscreen = () => {
    setIsFullscreen(false);
    // Restore body scroll
    document.body.style.overflow = '';
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  return { isFullscreen, enterFullscreen, exitFullscreen, toggleFullscreen };
};

// ============ Auto-Fit Text Component ============
// Automatically shrinks text to fit available space
const AutoFitText = ({ text, className, minFontSize = 12, maxFontSize = 16 }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(maxFontSize);
  const fittingRef = useRef(false);

  useEffect(() => {
    const fitText = () => {
      if (!containerRef.current || !textRef.current || fittingRef.current) return;

      const container = containerRef.current;
      const textEl = textRef.current;
      const containerHeight = container.clientHeight;

      // Skip if container has no height yet (still laying out)
      if (containerHeight < 20) return;

      fittingRef.current = true;

      // Reset to max font size
      let currentSize = maxFontSize;
      textEl.style.fontSize = `${currentSize}px`;

      // Force layout recalculation
      void textEl.offsetHeight;

      // Shrink until text fits or we hit minimum
      let iterations = 0;
      const maxIterations = (maxFontSize - minFontSize) * 2 + 10;

      while (
        textEl.scrollHeight > containerHeight &&
        currentSize > minFontSize &&
        iterations < maxIterations
      ) {
        currentSize -= 0.5;
        textEl.style.fontSize = `${currentSize}px`;
        iterations++;
      }

      setFontSize(currentSize);
      fittingRef.current = false;
    };

    // Multiple attempts to ensure layout is stable
    const timers = [
      setTimeout(fitText, 50),
      setTimeout(fitText, 150),
      setTimeout(fitText, 300)
    ];

    // Use ResizeObserver for more reliable size detection
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        setTimeout(fitText, 10);
      });
      resizeObserver.observe(containerRef.current);
    }

    // Re-fit on resize
    window.addEventListener('resize', fitText);

    return () => {
      timers.forEach(t => clearTimeout(t));
      window.removeEventListener('resize', fitText);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [text, minFontSize, maxFontSize]);

  // Calculate line height based on font size - smaller fonts can be tighter
  const lineHeight = fontSize <= 10 ? 1.35 : fontSize <= 12 ? 1.4 : 1.5;

  return (
    <div ref={containerRef} className={className} style={{ overflow: 'hidden' }}>
      <p
        ref={textRef}
        style={{
          fontSize: `${fontSize}px`,
          lineHeight: lineHeight,
          fontWeight: 500,
          margin: 0,
          fontFamily: 'Quicksand, sans-serif',
          color: 'var(--st-text-body)'
        }}
      >
        {text}
      </p>
    </div>
  );
};

// ============ Layout Components ============
// Full-page image with text overlay - like real children's books

const ImageWithTextLayout = ({ page, isFullscreen }) => {
  const textLength = page.text?.length || 0;

  // Dynamic font sizing based on text length
  let fontSize;
  if (textLength < 150) {
    fontSize = 14;
  } else if (textLength < 300) {
    fontSize = 13;
  } else if (textLength < 450) {
    fontSize = 12;
  } else if (textLength < 600) {
    fontSize = 11;
  } else {
    fontSize = 10;
  }

  const hasVideo = page.video_url && page.has_video;
  const imageUrl = getImageUrl(page.image_url);
  const videoUrl = getVideoUrl(page.video_url);

  // Same overlay layout in all modes for consistency
  // Fullscreen CSS (.fullscreen-mode) handles scrolling for longer text
  return (
    <div className="fullpage-layout">
      {/* Full-page background image/video or loading placeholder */}
      {page.image_url ? (
        hasVideo && videoUrl ? (
          <VideoPlayer
            videoUrl={videoUrl}
            posterUrl={imageUrl}
            className="fullpage-image"
            autoPlay={true}
            loop={true}
          />
        ) : (
          <img
            src={imageUrl}
            alt={`Page ${page.page_number}`}
            className="fullpage-image"
          />
        )
      ) : (
        <div className="fullpage-image-loading">
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <Loader2 className="w-8 h-8 mb-2 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
              Creating this page...
            </p>
          </div>
        </div>
      )}
      {/* Text overlay - scrollable in fullscreen if text is long */}
      <div className="text-overlay" style={{ fontSize: `${fontSize}px` }}>
        <p>{page.text}</p>
      </div>
    </div>
  );
};

const TextOnlyLayout = ({ page, isFullscreen }) => (
  <div className={`text-only-layout ${isFullscreen ? 'fullscreen-text-only' : ''}`}>
    <p>{page.text}</p>
  </div>
);

// ============ Professional Layout Components ============
// These components handle the enhanced layout system with speech bubbles,
// text zones, and emphasis words for professional picture book layouts

// TextElement - renders text with optional emphasis words styled bold/large
const TextElement = ({ content, type, emphasis_words = [] }) => {
  // Render content with emphasis words highlighted
  const renderWithEmphasis = (text) => {
    if (!text) return text;

    // First, clean any raw markers that might be in the text from LLM output
    let cleanedText = text.replace(/%%EMPHASIS_START%%|%%EMPHASIS_END%%/g, '');

    // Build a combined regex pattern for all emphasis words (deduplicated, case-insensitive)
    const uniqueWords = [...new Set((emphasis_words || []).map(w => w.toLowerCase()))];
    if (uniqueWords.length === 0) return cleanedText;

    // Escape regex special chars and join with OR
    const escapedWords = uniqueWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escapedWords.join('|')})`, 'gi');

    // Split by the pattern - this captures the matched words as separate array elements
    const parts = cleanedText.split(pattern);

    // Map parts to either plain text or styled spans
    return parts.map((part, i) => {
      // Check if this part matches any emphasis word (case-insensitive)
      const isEmphasis = uniqueWords.some(word => part.toLowerCase() === word.toLowerCase());
      if (isEmphasis) {
        return <span key={i} className="emphasis-word">{part}</span>;
      }
      return part;
    });
  };

  const typeClasses = {
    narrative: "text-pro-narrative",
    dialogue: "text-pro-dialogue",
    sound_effect: "text-pro-sound-effect",
  };

  return (
    <p className={typeClasses[type] || typeClasses.narrative}>
      {renderWithEmphasis(content)}
    </p>
  );
};

// SpeechBubble - comic-style speech bubble for character dialogue
const SpeechBubble = ({ text, speaker, position, style = "speech", emphasis_words = [] }) => {
  const positionClasses = {
    "top-left": "speech-bubble-top-left",
    "top-right": "speech-bubble-top-right",
    "bottom-left": "speech-bubble-bottom-left",
    "bottom-right": "speech-bubble-bottom-right",
  };

  const tailClasses = {
    "top-left": "tail-bottom-left",
    "top-right": "tail-bottom-right",
    "bottom-left": "tail-top-left",
    "bottom-right": "tail-top-right",
  };

  const styleClasses = {
    speech: "bubble-speech",
    thought: "bubble-thought",
    shout: "bubble-shout",
  };

  // Render text with emphasis (sanitizes any raw markers from LLM output)
  const renderWithEmphasis = (textContent) => {
    if (!textContent) return textContent;

    // First, clean any raw markers that might be in the text
    let cleanedText = textContent.replace(/%%EMPHASIS_START%%|%%EMPHASIS_END%%/g, '');

    // Build a combined regex pattern for all emphasis words (deduplicated, case-insensitive)
    const uniqueWords = [...new Set((emphasis_words || []).map(w => w.toLowerCase()))];
    if (uniqueWords.length === 0) return cleanedText;

    // Escape regex special chars and join with OR
    const escapedWords = uniqueWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escapedWords.join('|')})`, 'gi');

    // Split by the pattern - this captures the matched words as separate array elements
    const parts = cleanedText.split(pattern);

    // Map parts to either plain text or styled spans
    return parts.map((part, i) => {
      const isEmphasis = uniqueWords.some(word => part.toLowerCase() === word.toLowerCase());
      if (isEmphasis) {
        return <span key={i} className="emphasis-word">{part}</span>;
      }
      return part;
    });
  };

  return (
    <div className={`speech-bubble ${positionClasses[position] || ''} ${styleClasses[style] || ''} ${tailClasses[position] || ''}`}>
      <p>{renderWithEmphasis(text)}</p>
    </div>
  );
};

// TextOverlay - positioned text overlay for professional layouts
// Dynamically adjusts font size based on total text length
const TextOverlay = ({ textElements, position = "overlay-bottom" }) => {
  const positionClasses = {
    "overlay-bottom": "text-overlay-pro text-overlay-bottom",
    "overlay-top": "text-overlay-pro text-overlay-top",
    "left-panel": "text-overlay-pro text-overlay-left",
    "right-panel": "text-overlay-pro text-overlay-right",
  };

  // Calculate total text length for dynamic sizing
  const totalLength = textElements.reduce((sum, el) => sum + (el.content?.length || 0), 0);

  // Dynamic font size class based on text length
  let sizeClass = '';
  if (totalLength > 350) {
    sizeClass = 'text-overlay-xs';
  } else if (totalLength > 250) {
    sizeClass = 'text-overlay-sm';
  } else if (totalLength > 150) {
    sizeClass = 'text-overlay-md';
  }

  return (
    <div className={`${positionClasses[position] || positionClasses["overlay-bottom"]} ${sizeClass}`}>
      {textElements.map((element, idx) => (
        <TextElement
          key={idx}
          content={element.content}
          type={element.type}
          emphasis_words={element.emphasis_words}
        />
      ))}
    </div>
  );
};

// EnhancedLayout - professional picture book layout with text zones
// Uses the same overlay layout in both normal and fullscreen modes
const EnhancedLayout = ({ page, isFullscreen }) => {
  const textZone = page.layout?.text_zone || "overlay-bottom";
  const hasVideo = page.video_url && page.has_video;
  const imageUrl = getImageUrl(page.spread_image_url || page.image_url);
  const videoUrl = getVideoUrl(page.video_url);

  // CRITICAL: Use page.text as the single source of truth for text content
  // This ensures visual display matches audio narration (both use page.text)
  // Only use text_elements for emphasis_words styling, NOT for additional content
  // This prevents duplicate text from appearing
  const correctedTextElements = page.text
    ? [{
        content: page.text,
        type: 'narrative',
        // Merge emphasis_words from all text_elements for styling
        emphasis_words: page.text_elements?.flatMap(el => el.emphasis_words || []) || []
      }]
    : page.text_elements && page.text_elements.length > 0
      ? [page.text_elements[0]] // Fallback to first text_element only
      : [];

  return (
    <div className={`enhanced-layout ${isFullscreen ? 'enhanced-fullscreen' : ''}`}>
      {/* Full-page background image or video */}
      {page.image_url && (
        hasVideo && videoUrl ? (
          <VideoPlayer
            videoUrl={videoUrl}
            posterUrl={imageUrl}
            className="fullpage-image"
            autoPlay={true}
            loop={true}
          />
        ) : (
          <img
            src={imageUrl}
            alt={`Page ${page.page_number}`}
            className="fullpage-image"
          />
        )
      )}

      {/* Text overlay positioned according to layout */}
      {correctedTextElements.length > 0 && (
        <TextOverlay
          textElements={correctedTextElements}
          position={textZone}
        />
      )}
    </div>
  );
};

// ============ Chapter Book Layout Components (for ages 8-12) ============

// ChapterImageOnlyPage - Full-page chapter illustration (no text overlay)
// Used as the opening page for each chapter
const ChapterImageOnlyPage = ({ page, isFullscreen }) => (
  <div className={`chapter-image-only-page ${isFullscreen ? 'chapter-fullscreen' : ''}`}>
    {page.image_url ? (
      <img
        src={getImageUrl(page.spread_image_url || page.image_url)}
        alt={`Chapter ${page.chapter_number} illustration`}
        className="chapter-full-image"
      />
    ) : (
      <div className="chapter-image-placeholder">
        <Sparkles className="w-12 h-12" style={{ color: '#FFB088' }} />
      </div>
    )}
  </div>
);

// ChapterTextWithTitlePage - Chapter title at top (1/3) + text below (2/3)
// Used as the first text page of each chapter, following the image page
const ChapterTextWithTitlePage = ({ page, isFullscreen }) => (
  <div className={`chapter-text-with-title-page ${isFullscreen ? 'chapter-text-fullscreen' : ''}`}>
    {/* Chapter Title Header - Top 1/3 */}
    <div className="chapter-title-header">
      <span className="chapter-number-label">Chapter {page.chapter_number}</span>
      <h2 className="chapter-title-heading">{page.chapter_title || page.title || `Chapter ${page.chapter_number}`}</h2>
    </div>
    {/* Text Content - Bottom 2/3 */}
    <div className="chapter-text-body">
      <p className="chapter-text">{page.text}</p>
    </div>
  </div>
);

// ChapterTitlePage - LEGACY: Title page for a chapter with illustration overlay
// Keeping for backwards compatibility but no longer used for new books
const ChapterTitlePage = ({ page, isFullscreen }) => (
  <div className={`chapter-title-page ${isFullscreen ? 'chapter-title-fullscreen' : ''}`}>
    {page.image_url && (
      <img
        src={getImageUrl(page.image_url)}
        alt={`Chapter ${page.chapter_number}`}
        className="chapter-title-image"
      />
    )}
    <div className="chapter-title-overlay">
      <span className="chapter-number-badge">
        Chapter {page.chapter_number}
      </span>
      <h2 className="chapter-title-text">{page.chapter_title || page.title}</h2>
    </div>
  </div>
);

// ChapterImagePage - Full-page illustration with no text overlay
const ChapterImagePage = ({ page, isFullscreen }) => (
  <div className={`chapter-image-page ${isFullscreen ? 'chapter-fullscreen' : ''}`}>
    {page.image_url ? (
      <img
        src={getImageUrl(page.spread_image_url || page.image_url)}
        alt={`Page ${page.page_number}`}
        className="chapter-full-image"
      />
    ) : (
      <div className="chapter-image-placeholder">
        <Sparkles className="w-12 h-12" style={{ color: '#FFB088' }} />
      </div>
    )}
  </div>
);

// ChapterTextPage - Text-only page for chapter book style (no title header)
const ChapterTextPage = ({ page, isFullscreen }) => (
  <div className={`chapter-text-page ${isFullscreen ? 'chapter-text-fullscreen' : ''}`}>
    <div className="chapter-text-content">
      <p className="chapter-text">{page.text}</p>
    </div>
  </div>
);

// ============ Page Component (forwardRef required for react-pageflip) ============
const PageSpread = forwardRef(({ page, previousPage, pageNumber, isFullscreen }, ref) => {
  const hasImage = !!page?.image_url;
  const layoutType = page?.layout?.layout_type;

  // Check if this page has enhanced layout data (professional layouts)
  const hasEnhancedLayout = page?.text_elements && page.text_elements.length > 0;

  // Chapter book detection
  // Structured chapter books have chapter_number set on all pages
  // Interval-based chapter books (8-12 without explicit chapters) have is_chapter_title_page on image pages
  const isChapterBook = page?.chapter_number !== undefined && page?.chapter_number !== null;
  const isChapterTitlePage = page?.is_chapter_title_page;
  const isFirstChapterTextPage = previousPage?.is_chapter_title_page === true && !isChapterTitlePage;

  // Determine which layout to use
  const renderLayout = () => {
    // Chapter title pages (image-only full page) - both structured and interval-based
    // This handles interval-based chapter books where is_chapter_title_page is set for image pages
    if (isChapterTitlePage) {
      return <ChapterImageOnlyPage page={page} isFullscreen={isFullscreen} />;
    }

    // Chapter book layouts (structured chapter books for ages 8-12)
    if (isChapterBook) {
      // First text page of chapter: Chapter title header (1/3) + text (2/3)
      if (isFirstChapterTextPage) {
        return <ChapterTextWithTitlePage page={page} isFullscreen={isFullscreen} />;
      }

      // Subsequent chapter text pages: Text only (no header)
      return <ChapterTextPage page={page} isFullscreen={isFullscreen} />;
    }

    // Interval-based chapter book text pages (no image, text only)
    // These are pages in 8-12 books where is_chapter_title_page is false
    if (previousPage?.is_chapter_title_page === true && !hasImage) {
      return <ChapterTextPage page={page} isFullscreen={isFullscreen} />;
    }

    // Legacy chapter book layouts (8-12 age range) - for older stories
    if (layoutType === 'chapter-image') {
      return <ChapterImagePage page={page} isFullscreen={isFullscreen} />;
    }
    if (layoutType === 'chapter-text') {
      return <ChapterTextPage page={page} isFullscreen={isFullscreen} />;
    }

    // Professional picture book layouts (only if there's an image)
    if (hasEnhancedLayout && hasImage) {
      // Use professional layout with text zones and speech bubbles
      return <EnhancedLayout page={page} isFullscreen={isFullscreen} />;
    } else if (hasImage) {
      // Fallback to basic image with text overlay
      return <ImageWithTextLayout page={page} isFullscreen={isFullscreen} />;
    } else {
      // Text-only layout (chapter book pages without images)
      return <TextOnlyLayout page={page} isFullscreen={isFullscreen} />;
    }
  };

  return (
    <div ref={ref} className="page-spread">
      {renderLayout()}

      <span className={`page-num ${pageNumber % 2 === 0 ? 'left' : 'right'}`}>
        {pageNumber}
      </span>
    </div>
  );
});

PageSpread.displayName = 'PageSpread';

// ============ The End Page Component ============
const TheEndPage = forwardRef(({ story, pageNumber, isFullscreen, onCreateStory, onExplore, onReadAgain }, ref) => {
  return (
    <div
      ref={ref}
      className="page-spread the-end-page"
    >
      {/* Inner container to properly position content within the page */}
      <div
        className="flex flex-col items-center justify-center text-center h-full w-full"
        style={{ padding: isFullscreen ? '0.5rem' : '1rem' }}
      >
        {/* Decorative stars */}
        <div className="flex items-center justify-center gap-2 mb-1">
          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
        </div>

        {/* The End Title */}
        <h2
          className="font-bold mb-1"
          style={{
            fontFamily: 'Chewy, cursive',
            color: 'var(--st-text-heading)',
            fontSize: isFullscreen ? '1.5rem' : '2rem'
          }}
        >
          The End
        </h2>

        {/* Decorative swirl */}
        <div className="flex items-center justify-center gap-2 mb-2 w-full">
          <div className="w-10 h-0.5 rounded-full" style={{ background: '#FFB347' }} />
          <Sparkles className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--st-primary)' }} />
          <div className="w-10 h-0.5 rounded-full" style={{ background: '#FFB347' }} />
        </div>

        {/* Moral of the Story */}
        {story?.structure?.moral && (
          <div className="mb-2 px-1">
            <p
              className="font-bold mb-0.5 uppercase tracking-widest"
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: 'var(--st-primary)',
                fontSize: isFullscreen ? '0.55rem' : '0.65rem'
              }}
            >
              Moral of the Story
            </p>
            <p
              className="italic leading-snug"
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: 'var(--st-text-heading)',
                fontSize: isFullscreen ? '0.7rem' : '0.8rem'
              }}
            >
              "{story.structure.moral}"
            </p>
          </div>
        )}

        {/* Theme & Category Tags */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 mb-2">
          {/* Only show theme tag if it's short (keyword-style, not a sentence) */}
          {story?.structure?.theme && story.structure.theme.length <= 30 && (
            <span
              className="px-2 py-0.5 rounded-full font-bold the-end-theme-tag"
              style={{ fontSize: isFullscreen ? '0.55rem' : '0.65rem' }}
            >
              {story.structure.theme}
            </span>
          )}
          {story?.category && (
            <span
              className="px-2 py-0.5 rounded-full font-bold the-end-category-tag"
              style={{ fontSize: isFullscreen ? '0.55rem' : '0.65rem' }}
            >
              {story.category}
            </span>
          )}
        </div>

        {/* Thank you message */}
        <p
          className="font-medium mb-3"
          style={{
            fontFamily: 'Quicksand, sans-serif',
            color: 'var(--st-text-muted)',
            fontSize: isFullscreen ? '0.65rem' : '0.75rem'
          }}
        >
          Thank you for reading!
        </p>

        {/* Action Buttons - 2x2 grid layout */}
        <div className="grid grid-cols-2 gap-1.5 w-full max-w-xs">
          {/* Read Again - Primary action */}
          <button
            onClick={onReadAgain}
            className="py-1.5 px-3 rounded-lg font-bold active:scale-95 transition-transform flex items-center justify-center gap-1"
            style={{
              background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
              color: 'white',
              fontFamily: 'Chewy, cursive',
              fontSize: isFullscreen ? '0.65rem' : '0.75rem'
            }}
          >
            <RotateCcw className="w-3 h-3" />
            Read Again
          </button>
          <button
            onClick={onCreateStory}
            className="py-1.5 px-3 rounded-lg font-bold active:scale-95 transition-transform the-end-secondary-btn"
            style={{
              fontFamily: 'Chewy, cursive',
              fontSize: isFullscreen ? '0.65rem' : '0.75rem',
            }}
          >
            Create New
          </button>
          <button
            onClick={onExplore}
            className="py-1.5 px-3 rounded-lg font-bold active:scale-95 transition-transform the-end-secondary-btn"
            style={{
              fontFamily: 'Chewy, cursive',
              fontSize: isFullscreen ? '0.65rem' : '0.75rem',
            }}
          >
            Explore
          </button>
          {/* Print This Book - Coming Soon */}
          <button
            disabled
            className="py-1.5 px-3 rounded-lg font-bold flex items-center justify-center gap-1 opacity-50 cursor-not-allowed"
            style={{
              background: 'var(--st-bg-page)',
              color: 'var(--st-text-heading)',
              fontFamily: 'Chewy, cursive',
              fontSize: isFullscreen ? '0.65rem' : '0.75rem',
              border: '1px solid var(--st-border-light)'
            }}
          >
            <Printer className="w-3 h-3" style={{ color: 'var(--st-text-muted)' }} />
            Print
            <span
              className="px-1 py-0.5 rounded-full font-semibold bg-orange-100 dark:bg-orange-900/30"
              style={{
                color: 'var(--st-primary)',
                fontSize: '0.5rem',
                fontFamily: 'Quicksand, sans-serif'
              }}
            >
              Soon
            </span>
          </button>
        </div>
      </div>
    </div>
  );
});

TheEndPage.displayName = 'TheEndPage';

// ============ Front Cover Page Component ============
const FrontCoverPage = forwardRef(({ story, isFullscreen, isStreaming }, ref) => {
  const coverImage = story?.thumbnail || story?.book_cover;
  const imageUrl = coverImage?.startsWith('/static/')
    ? `${process.env.REACT_APP_BACKEND_URL}${coverImage}`
    : coverImage;

  return (
    <div
      ref={ref}
      className="page-spread relative overflow-hidden"
      style={{
        // Use a warm background to fill any gaps when using object-contain
        background: coverImage
          ? 'linear-gradient(180deg, var(--st-bg-page) 0%, var(--st-border-light) 100%)'
          : 'linear-gradient(135deg, #8B6914 0%, #C4A052 40%, #D4AF37 70%, #8B6914 100%)'
      }}
    >
      {/* Cover Image - always use contain to show full cover with title */}
      {coverImage ? (
        <img
          src={imageUrl}
          alt={story?.title}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'contain' }}
        />
      ) : (
        /* Fallback when no cover image - show elegant placeholder */
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          {/* Decorative book frame */}
          <div
            className="absolute inset-4 rounded-lg"
            style={{
              border: '3px double rgba(255,248,220,0.4)',
              background: 'linear-gradient(180deg, rgba(139,105,20,0.3) 0%, rgba(196,160,82,0.2) 50%, rgba(139,105,20,0.3) 100%)'
            }}
          />

          {/* Book icon */}
          <div className="relative z-10 mb-4">
            <BookOpen
              className={`w-16 h-16 ${isStreaming ? 'animate-pulse' : ''}`}
              style={{ color: 'rgba(255,248,220,0.8)' }}
            />
          </div>

          <h1
            className="relative z-10 font-bold text-white mb-3 drop-shadow-lg"
            style={{
              fontFamily: 'Chewy, cursive',
              fontSize: isFullscreen ? '1.25rem' : '1.5rem',
              textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
              maxWidth: '85%',
              lineHeight: 1.3
            }}
          >
            {story?.title}
          </h1>

          <p
            className="relative z-10 text-white/90 drop-shadow mb-4"
            style={{
              fontFamily: 'Quicksand, sans-serif',
              fontSize: isFullscreen ? '0.7rem' : '0.8rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            by {story?.author_name || 'Anonymous'}
          </p>

          {/* Generating indicator */}
          {isStreaming && (
            <div className="relative z-10 flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <Sparkles className="w-3 h-3 text-yellow-300 animate-pulse" />
              <span
                className="text-white/90 text-xs"
                style={{ fontFamily: 'Quicksand, sans-serif' }}
              >
                Cover generating...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

FrontCoverPage.displayName = 'FrontCoverPage';

// ============ Blank Page Component (for traditional book layout) ============
const BlankPage = forwardRef(({ style = {} }, ref) => {
  return (
    <div
      ref={ref}
      className="page-spread"
      style={{
        background: 'linear-gradient(180deg, #FFFEF9 0%, var(--st-bg-page) 50%, var(--st-border-light) 100%)',
        ...style
      }}
    >
      {/* Empty page - represents inside of book cover or padding page */}
    </div>
  );
});

BlankPage.displayName = 'BlankPage';

// ============ Back Cover Page Component ============
const BackCoverPage = forwardRef(({ story, isFullscreen }, ref) => {
  return (
    <div
      ref={ref}
      className="page-spread flex flex-col items-center justify-center text-center"
      style={{
        background: 'linear-gradient(135deg, #E86A33 0%, #F28C5A 50%, #FFB347 100%)',
        padding: isFullscreen ? '1rem' : '1.5rem',
        overflow: 'hidden'
      }}
    >
      {/* Logo/Brand area */}
      <div className={isFullscreen ? 'mb-3' : 'mb-4'}>
        <div
          className="rounded-full flex items-center justify-center mx-auto mb-2"
          style={{
            background: 'rgba(255,255,255,0.2)',
            width: isFullscreen ? '3rem' : '4rem',
            height: isFullscreen ? '3rem' : '4rem'
          }}
        >
          <BookOpen
            className="text-white"
            style={{ width: isFullscreen ? '1.5rem' : '2rem', height: isFullscreen ? '1.5rem' : '2rem' }}
          />
        </div>
        <h3
          className="font-bold text-white"
          style={{
            fontFamily: 'Chewy, cursive',
            fontSize: isFullscreen ? '1rem' : '1.25rem'
          }}
        >
          Tellor
        </h3>
        <p
          className="text-white/80 mt-0.5"
          style={{
            fontFamily: 'Quicksand, sans-serif',
            fontSize: isFullscreen ? '0.6rem' : '0.7rem'
          }}
        >
          Where stories come alive
        </p>
      </div>

      {/* Story info */}
      <div
        className="px-3 py-2 rounded-xl mb-3"
        style={{ background: 'rgba(255,255,255,0.15)' }}
      >
        <p
          className="font-bold text-white truncate"
          style={{
            fontFamily: 'Quicksand, sans-serif',
            fontSize: isFullscreen ? '0.75rem' : '0.85rem',
            maxWidth: isFullscreen ? '10rem' : '12rem'
          }}
        >
          {story?.title}
        </p>
        <p
          className="text-white/70 mt-0.5"
          style={{
            fontFamily: 'Quicksand, sans-serif',
            fontSize: isFullscreen ? '0.6rem' : '0.7rem'
          }}
        >
          by {story?.author_name || 'Anonymous'}
        </p>
      </div>

      {/* Decorative stars */}
      <div className="flex items-center gap-1.5">
        <Star className="w-2.5 h-2.5 text-white/50 fill-white/50" />
        <Star className="w-3 h-3 text-white/70 fill-white/70" />
        <Star className="w-2.5 h-2.5 text-white/50 fill-white/50" />
      </div>
    </div>
  );
});

BackCoverPage.displayName = 'BackCoverPage';

// Visual preset to display label mapping
const VISUAL_PRESET_LABELS = {
  "classic-storybook": "Classic Storybook",
  "soft-watercolor": "Soft Watercolor",
  "comic-adventure": "Comic Adventure",
  "modern-minimalist": "Modern Minimalist",
  "dreamy-pastel": "Dreamy Pastel",
  "realistic-painted": "Realistic Painted",
  "cinematic-3d": "Cinematic 3D",
  "biblical-epic": "Biblical Epic",
};

// Art style to display label mapping (fallback for older stories)
const ART_STYLE_LABELS = {
  storybook: "Classic Storybook",
  watercolor: "Watercolor",
  cartoon: "Cartoon",
  "3d-render": "3D Render",
  anime: "Anime",
  "pencil-sketch": "Pencil Sketch",
};

// Get display label for art style
const getArtStyleLabel = (story) => {
  if (!story) return "Classic Storybook";
  // Prefer visual_preset if available
  if (story.visual_preset && VISUAL_PRESET_LABELS[story.visual_preset]) {
    return VISUAL_PRESET_LABELS[story.visual_preset];
  }
  // Fall back to art_style
  if (story.art_style && ART_STYLE_LABELS[story.art_style]) {
    return ART_STYLE_LABELS[story.art_style];
  }
  // Last resort: format the raw value or show default
  if (story.art_style) {
    return story.art_style.replace(/_/g, " ").replace(/-/g, " ");
  }
  return "Classic Storybook";
};

// ============ Main StoryViewer Component ============
const StoryViewer = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shouldContinueReading = searchParams.get('continue') === 'true';
  const isStreamingMode = searchParams.get('streaming') === 'true';
  const bookRef = useRef();
  const fullscreenRef = useRef();
  const storyStreamRef = useRef(null); // SSE connection for streaming updates

  // Fullscreen mode
  const { isFullscreen, toggleFullscreen } = useFullscreen(fullscreenRef);

  const [story, setStory] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [isFavorited, setIsFavorited] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [regeneratingImages, setRegeneratingImages] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showStoryDetails, setShowStoryDetails] = useState(false);
  const [showStoryboard, setShowStoryboard] = useState(false);
  const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);
  const [editingDetailsField, setEditingDetailsField] = useState(null); // 'title' or 'category'
  const [editedDetailsTitle, setEditedDetailsTitle] = useState('');
  const [editedDetailsCategory, setEditedDetailsCategory] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [regenerateProgress, setRegenerateProgress] = useState({ current: 0, total: 0, message: '' });
  const [bookDimensions, setBookDimensions] = useState({ width: 420, height: 560 });
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [use3DBook, setUse3DBook] = useState(false);
  const book3DRef = useRef(null);

  // Progressive story viewing (streaming) state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingProgress, setStreamingProgress] = useState({
    readyPages: 0,
    pagesWithAudio: 0,
    totalPages: 0,
  });

  // Read to Me (TTS) state
  const [isReadingMode, setIsReadingMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [readingComplete, setReadingComplete] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const abortControllerRef = useRef(null);
  const audioCache = useRef(new Map()); // Cache for prefetched audio
  const prefetchAbortRef = useRef(null); // Abort controller for prefetch requests
  const justEnabledReadingRef = useRef(false); // Track if we just enabled reading mode
  const pageBeforeFullscreenRef = useRef(null); // Track page before fullscreen toggle

  // Check if story has pre-generated audio (premium feature)
  const storyHasAudio = story?.pages?.some(page => page.audio_url) || false;

  // Restore page position after fullscreen toggle (component remounts due to key change)
  useEffect(() => {
    if (pageBeforeFullscreenRef.current !== null && bookRef.current) {
      // Small delay to let the book fully initialize after remount
      const timer = setTimeout(() => {
        const pageFlip = bookRef.current?.pageFlip();
        if (pageFlip) {
          pageFlip.turnToPage(pageBeforeFullscreenRef.current);
          pageBeforeFullscreenRef.current = null;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFullscreen]);

  // Restore saved reading progress when story loads (only if ?continue=true in URL)
  const savedProgressRestoredRef = useRef(false);
  useEffect(() => {
    if (story && bookRef.current && !loading && !savedProgressRestoredRef.current) {
      // Only restore progress if explicitly requested via ?continue=true
      if (shouldContinueReading) {
        const savedProgress = localStorage.getItem(`readingProgress_${storyId}`);
        if (savedProgress) {
          try {
            const { page } = JSON.parse(savedProgress);
            if (page > 0) {
              // Small delay to ensure book is ready
              const timer = setTimeout(() => {
                const pageFlip = bookRef.current?.pageFlip();
                if (pageFlip) {
                  pageFlip.turnToPage(page);
                  setCurrentPage(page);
                }
              }, 300);
              savedProgressRestoredRef.current = true;
              return () => clearTimeout(timer);
            }
          } catch (e) {
            // Invalid saved progress, ignore
          }
        }
      }
      savedProgressRestoredRef.current = true;
    }
  }, [story, loading, storyId, shouldContinueReading]);

  // Convert page index between 2D book and 3D book formats
  // 2D book (desktop): [blank, cover, page0, page1, page2, ...] - FRONT_COVER_OFFSET = 2
  // 3D book: spread 0 = cover, spread 1 = pages 0-1, spread 2 = pages 2-3, ...
  const convert2DPageTo3D = (page2D) => {
    if (page2D <= 1) return 0; // Cover
    const storyIdx = page2D - 2; // FRONT_COVER_OFFSET for desktop
    return Math.floor(storyIdx / 2) + 1;
  };

  const convert3DPageTo2D = (page3D) => {
    if (page3D === 0) return 0; // Cover
    const storyIdx = (page3D - 1) * 2;
    return storyIdx + 2; // Add FRONT_COVER_OFFSET for desktop
  };

  // Toggle traditional fullscreen mode (expands 2D book)
  const handleToggleFullscreen = () => {
    pageBeforeFullscreenRef.current = currentPage;
    toggleFullscreen();
  };

  // Toggle 3D book mode (separate from fullscreen)
  const handleToggle3DBook = () => {
    pageBeforeFullscreenRef.current = currentPage;

    if (use3DBook) {
      // Exiting 3D mode - convert 3D page to 2D page
      const page2D = convert3DPageTo2D(currentPage);
      setCurrentPage(page2D);
      setUse3DBook(false);
    } else {
      // Entering 3D mode - convert 2D page to 3D page
      const page3D = convert2DPageTo3D(currentPage);
      setCurrentPage(page3D);
      setUse3DBook(true);
    }
  };

  // Check if 3D book is available (tablet+ with WebGL, only picture books and easy readers)
  // Chapter books (8-12) don't have images on all pages, so 3D view won't work
  const isPictureBookOrEasyReader = story?.age_range === '3-5' || story?.age_range === '5-8';
  const is3DBookAvailable = isTabletOrLarger() && hasWebGLSupport() && isPictureBookOrEasyReader;

  // Calculate responsive book dimensions - fit within viewport without scrolling
  useEffect(() => {
    const updateDimensions = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);

      // In fullscreen mode, use the SAME aspect ratio as normal mode, just scaled up
      // This ensures content isn't cropped differently than in normal view
      if (isFullscreen) {
        const fullscreenWidth = window.innerWidth;
        const fullscreenHeight = window.innerHeight;

        if (isMobile) {
          // Mobile fullscreen - maximize book space
          // Reserve: nav row with buttons (45px) + page indicator (30px) + safe area (~20px) = ~95px
          const targetAspectRatio = 400 / 580; // ~0.69
          const availableWidth = fullscreenWidth - 12; // Minimal side margins
          const availableHeight = fullscreenHeight - 100;

          let pageWidth = Math.min(availableWidth, 500);
          let pageHeight = Math.floor(pageWidth / targetAspectRatio);

          // If height exceeds available, scale down proportionally
          if (pageHeight > availableHeight) {
            pageHeight = availableHeight;
            pageWidth = Math.floor(pageHeight * targetAspectRatio);
          }

          setBookDimensions({ width: pageWidth, height: pageHeight });
        } else {
          // Desktop/tablet fullscreen - TWO PAGE SPREAD with floating side nav buttons
          // Reserve: exit button (40px) + Read to Me (45px) + page indicator (50px) + margins (15px) = 150px vertical
          // Reserve: 80px on each side for floating nav buttons = 160px horizontal
          const availableHeight = fullscreenHeight - 150;
          const availableWidth = fullscreenWidth - 180; // Space for side buttons (90px each side)

          let pageWidth, pageHeight;

          if (fullscreenWidth <= 1400) {
            // iPad/Tablet fullscreen (768-1400px)
            pageWidth = Math.min(availableWidth / 2 - 8, 550);
            pageHeight = Math.min(availableHeight, 700);
          } else if (fullscreenWidth < 1800) {
            // Desktop fullscreen
            pageWidth = Math.min(availableWidth / 2 - 16, 620);
            pageHeight = Math.min(availableHeight, 850);
          } else {
            // Widescreen fullscreen
            pageWidth = Math.min(availableWidth / 2 - 20, 720);
            pageHeight = Math.min(availableHeight, 950);
          }

          setBookDimensions({ width: pageWidth, height: pageHeight });
        }
        return;
      }

      // Normal mode calculations
      const sidebarWidth = isMobile ? 0 : 256;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let width, height;

      if (isMobile) {
        // Mobile: single page view
        const availableWidth = windowWidth - 40;
        const availableHeight = windowHeight - 180;
        width = Math.min(availableWidth, 400);
        height = Math.min(availableHeight, 580);
      } else {
        // Desktop/Tablet: two-page spread
        // Container is now wider (max-w-7xl) with reduced padding
        const availableWidth = windowWidth - sidebarWidth - 32; // minimal margins
        const availableHeight = windowHeight - 260; // header + nav

        // Two pages side by side
        let pageWidth = Math.floor((availableWidth - 24) / 2); // small buffer for spine
        let pageHeight = Math.floor(availableHeight - 24);

        // Maintain reasonable aspect ratio
        const maxAspect = 0.75;
        const minAspect = 0.6;
        const currentAspect = pageWidth / pageHeight;

        if (currentAspect > maxAspect) {
          pageWidth = Math.floor(pageHeight * maxAspect);
        } else if (currentAspect < minAspect) {
          pageHeight = Math.floor(pageWidth / minAspect);
        }

        width = pageWidth;
        height = pageHeight;
      }

      setBookDimensions({ width, height });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isFullscreen]);

  // Keyboard navigation + ESC to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isEditingTitle) return;

      // ESC to exit fullscreen or 3D mode
      if (e.key === 'Escape') {
        if (use3DBook) {
          e.preventDefault();
          handleToggle3DBook();
          return;
        }
        if (isFullscreen) {
          e.preventDefault();
          handleToggleFullscreen();
          return;
        }
      }

      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        flipNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        flipPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, story, isEditingTitle, isFullscreen]);

  // ============ Read to Me (TTS) Functions - OpenAI TTS with Prefetching ============

  // Front cover offset - story pages start at flipbook index 2 (after blank + cover pages)
  // Traditional book layout:
  // Desktop: [Blank|Cover] then [Page1|Page2], [Page3|Page4], etc.
  // Mobile: [Cover] then [Page1], [Page2], etc. (no blank pages needed for single-page view)
  // 3D Book: Page 0 = cover, Page 1 = pages[0]+pages[1], Page 2 = pages[2]+pages[3], etc.
  const FRONT_COVER_OFFSET = isMobileView ? 1 : 2;

  // Convert flipbook page index to story pages array index
  const getStoryPageIndex = (flipbookIndex) => {
    // 3D book has different page numbering
    if (use3DBook) {
      // Page 0 is cover, page 1+ are story spreads
      if (flipbookIndex <= 0) return -1;
      return (flipbookIndex - 1) * 2;
    }
    return flipbookIndex - FRONT_COVER_OFFSET;
  };

  // Check if current flipbook page is a story page (not cover/end pages)
  const isStoryPage = (flipbookIndex) => {
    const storyIdx = getStoryPageIndex(flipbookIndex);
    return storyIdx >= 0 && storyIdx < (story?.pages?.length || 0);
  };

  // Get text for a specific spread (using flipbook page index)
  // In mobile/portrait mode, only get text for the single visible page
  // Skip chapter-image pages (image-only, text is not displayed)
  const getSpreadText = (flipbookIndex) => {
    if (!story?.pages) return '';

    const storyIdx = getStoryPageIndex(flipbookIndex);
    if (storyIdx < 0) return ''; // Front cover has no text

    const leftPage = story.pages[storyIdx];
    if (!leftPage) return ''; // Beyond story pages (The End, back cover)

    const isLeftChapterImage = leftPage?.layout?.layout_type === 'chapter-image';

    // In mobile view (portrait mode) with 2D book, only one page is visible
    // 3D book always shows spreads (two pages)
    if (isMobileView && !use3DBook) {
      return isLeftChapterImage ? '' : (leftPage?.text || '');
    }

    // In desktop view (landscape mode) or 3D book, show spread (two pages)
    const rightPage = story.pages[storyIdx + 1];
    const isRightChapterImage = rightPage?.layout?.layout_type === 'chapter-image';

    let text = isLeftChapterImage ? '' : (leftPage?.text || '');
    if (rightPage && !isRightChapterImage) {
      text += (text ? ' ' : '') + (rightPage.text || '');
    }
    return text;
  };

  // Get text for current spread
  const getCurrentSpreadText = () => getSpreadText(currentPage);

  // Generate audio for text (returns blob URL)
  const generateAudio = async (text, signal) => {
    const response = await axios.post(`${API}/tts`, {
      text: text,
      voice: 'nova'
    }, {
      withCredentials: true,
      signal: signal
    });

    const audioData = response.data.audio;
    const audioBlob = new Blob(
      [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
      { type: 'audio/mp3' }
    );
    return URL.createObjectURL(audioBlob);
  };

  // Prefetch audio for the next page(s)
  // In mobile mode: prefetch next single page
  // In desktop mode or 3D book: prefetch next spread
  const prefetchNextSpread = async () => {
    // 3D book advances by 1 page (each page is a spread), 2D book by 1 or 2
    const nextFlipbookIndex = currentPage + (use3DBook ? 1 : (isMobileView ? 1 : 2));
    // Check if next page is still a story page
    if (!isStoryPage(nextFlipbookIndex)) return;

    const nextText = getSpreadText(nextFlipbookIndex);
    if (!nextText || audioCache.current.has(nextText)) return;

    // Cancel any existing prefetch
    if (prefetchAbortRef.current) {
      prefetchAbortRef.current.abort();
    }
    prefetchAbortRef.current = new AbortController();

    try {
      const audioUrl = await generateAudio(nextText, prefetchAbortRef.current.signal);
      audioCache.current.set(nextText, audioUrl);
    } catch (error) {
      // Silently ignore prefetch errors - non-critical
    }
  };

  // Get pre-generated audio URLs for current spread
  // In mobile/portrait mode, only get audio for the single visible page
  // Skip chapter-image pages (image-only, text is not displayed)
  const getPreGeneratedAudioUrls = () => {
    if (!story?.pages) return [];
    const storyIdx = getStoryPageIndex(currentPage);
    if (storyIdx < 0) return []; // Front cover has no audio

    const leftPage = story.pages[storyIdx];
    if (!leftPage) return []; // Beyond story pages
    const urls = [];

    // Skip chapter-image pages (no visible text)
    const isLeftChapterImage = leftPage?.layout?.layout_type === 'chapter-image';
    if (leftPage?.audio_url && !isLeftChapterImage) {
      urls.push(getAudioUrl(leftPage.audio_url));
    }

    // In desktop view (landscape mode) or 3D book, also get audio for right page of spread
    if (!isMobileView || use3DBook) {
      const rightPage = story.pages[storyIdx + 1];
      const isRightChapterImage = rightPage?.layout?.layout_type === 'chapter-image';
      if (rightPage?.audio_url && !isRightChapterImage) {
        urls.push(getAudioUrl(rightPage.audio_url));
      }
    }

    return urls;
  };

  // Play multiple audio files in sequence
  const playAudioSequence = async (urls) => {
    let playedAtLeastOne = false;

    for (let i = 0; i < urls.length; i++) {
      // Skip null/undefined URLs
      if (!urls[i]) continue;

      // Create new audio for this URL
      const audio = new Audio(urls[i]);
      audioRef.current = audio;

      try {
        await new Promise((resolve, reject) => {
          audio.onplay = () => {
            playedAtLeastOne = true;
            setIsSpeaking(true);
            setIsLoadingAudio(false);
          };
          audio.onended = resolve;
          audio.onerror = reject;
          audio.play().catch(reject);
        });
      } catch (error) {
        console.error('Error playing audio:', error);
        // Continue to next audio if one fails
      }
    }

    // All audio finished
    setIsSpeaking(false);

    // Only mark reading complete if at least one audio played successfully
    // This prevents auto-flipping when audio fails to load
    if (playedAtLeastOne) {
      setReadingComplete(true);
      prefetchNextSpread();
    } else {
      // All audio failed - don't auto-flip, user can manually advance
      console.warn('No audio could be played for this spread');
    }
  };

  // Start reading current spread using pre-generated or on-the-fly TTS
  const startReading = async () => {
    const text = getCurrentSpreadText();
    if (!text) return;

    // Stop any current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Cancel any pending main TTS request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setReadingComplete(false);

    try {
      // Get visible pages for this spread (using story page indices)
      const visiblePages = [];
      const storyIdx = getStoryPageIndex(currentPage);

      if (storyIdx >= 0) {
        const leftPage = story?.pages?.[storyIdx];
        if (leftPage) visiblePages.push({ page: leftPage, index: storyIdx });

        // In desktop mode or 3D book, also include right page (spreads show two pages)
        if (!isMobileView || use3DBook) {
          const rightPage = story?.pages?.[storyIdx + 1];
          if (rightPage) visiblePages.push({ page: rightPage, index: storyIdx + 1 });
        }
      }

      // Collect audio URLs for all visible pages
      // Use pre-generated if available, otherwise generate on-the-fly
      const audioUrls = [];
      let needsGeneration = false;

      for (const { page, index } of visiblePages) {
        // Skip chapter-image pages (image-only layout, text is not displayed)
        if (page.layout?.layout_type === 'chapter-image') {
          continue;
        }

        if (page.audio_url) {
          // Use pre-generated audio
          audioUrls.push(getAudioUrl(page.audio_url));
        } else if (page.text) {
          // Need to generate audio on-the-fly for this page
          needsGeneration = true;

          // Check cache first
          const cacheKey = `page_${index}_${page.text.substring(0, 50)}`;
          if (audioCache.current.has(cacheKey)) {
            audioUrls.push(audioCache.current.get(cacheKey));
          } else {
            // Generate audio on-the-fly
            setIsLoadingAudio(true);
            try {
              const generatedUrl = await generateAudio(page.text, abortControllerRef.current.signal);
              audioCache.current.set(cacheKey, generatedUrl);
              audioUrls.push(generatedUrl);
            } catch (genError) {
              console.error(`Failed to generate audio for page ${index + 1}:`, genError);
              // Continue with other pages even if one fails
            }
          }
        }
      }

      setIsLoadingAudio(false);

      if (audioUrls.length > 0) {
        await playAudioSequence(audioUrls);
        return;
      }

      // If we still have no audio, try generating for the combined text
      let audioUrl;

      // Check cache first
      if (audioCache.current.has(text)) {
        audioUrl = audioCache.current.get(text);
      } else {
        // Generate new audio
        setIsLoadingAudio(true);
        audioUrl = await generateAudio(text, abortControllerRef.current.signal);
        audioCache.current.set(text, audioUrl);
      }

      // Create and play audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsLoadingAudio(false);
        // Start prefetching next spread while this one plays
        prefetchNextSpread();
      };

      audio.onended = () => {
        setIsSpeaking(false);
        setReadingComplete(true);
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setIsLoadingAudio(false);
        toast.error('Failed to play audio');
      };

      await audio.play();

    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return;
      }
      console.error('TTS error:', error);
      setIsLoadingAudio(false);
      toast.error('Failed to generate audio. Please try again.');
    }
  };

  // Stop reading
  const stopReading = () => {
    // Cancel any pending TTS request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onplay = null;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
    }
    setIsSpeaking(false);
    setIsLoadingAudio(false);
    setReadingComplete(false);
  };

  // Toggle reading mode
  const toggleReadingMode = () => {
    if (isReadingMode) {
      // Turn off reading mode
      stopReading();
      setIsReadingMode(false);
    } else {
      // Turn on reading mode and start reading
      justEnabledReadingRef.current = true; // Mark that we just enabled it
      setIsReadingMode(true);
      startReading();
    }
  };

  // Auto-read when page changes (if in reading mode)
  useEffect(() => {
    if (isReadingMode && story && !isLoadingAudio) {
      // Skip if we just enabled reading mode (already started in toggleReadingMode)
      if (justEnabledReadingRef.current) {
        justEnabledReadingRef.current = false;
        return;
      }
      // Small delay to let the page flip animation complete
      const timer = setTimeout(() => {
        startReading();
      }, 900);
      return () => clearTimeout(timer);
    }
  }, [currentPage, isReadingMode, isLoadingAudio]);

  // Auto-advance to next page when audio finishes (with delay)
  useEffect(() => {
    if (!readingComplete || !isReadingMode) return;

    // Calculate if we're on the last spread
    const storyPageCount = story?.pages?.length || 0;
    let onLastSpread = false;
    if (use3DBook) {
      // 3D book: totalPages = 1 (cover) + ceil((pages-1)/2) spreads + 1 (end)
      const total3DPages = 1 + Math.ceil((storyPageCount - 1) / 2) + 1;
      onLastSpread = currentPage >= total3DPages - 1;
    } else {
      const extraBlankBeforeEnd = isMobileView ? 0 : (storyPageCount % 2 === 1 ? 1 : 0);
      const totalPages = isMobileView
        ? 1 + storyPageCount + 1 + 1 // cover + story + end + back cover
        : 2 + storyPageCount + extraBlankBeforeEnd + 2; // blank+cover + story + (maybe blank) + end+back cover
      onLastSpread = isMobileView
        ? currentPage + 1 >= totalPages
        : currentPage + 2 >= totalPages;
    }

    if (onLastSpread) return;

    const timer = setTimeout(() => {
      // Flip to next page
      if (use3DBook && book3DRef.current) {
        book3DRef.current.flipNext();
      } else {
        flipNext();
      }
      setReadingComplete(false);
    }, 1500); // 1.5 second pause before flipping
    return () => clearTimeout(timer);
  }, [readingComplete, isReadingMode, currentPage, use3DBook, story?.pages?.length, isMobileView]);

  // Cleanup audio and pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (prefetchAbortRef.current) {
        prefetchAbortRef.current.abort();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Clean up cached audio URLs
      audioCache.current.forEach((url) => URL.revokeObjectURL(url));
      audioCache.current.clear();
    };
  }, []);

  useEffect(() => {
    loadStory();
  }, [storyId]);

  // Poll for video generation updates when videos are pending/generating
  useEffect(() => {
    if (!story?.video_status) return;
    if (story.video_status !== 'pending' && story.video_status !== 'generating') return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/v1/stories/${storyId}`, {
          withCredentials: true
        });
        const updatedStory = response.data;

        // Check if any videos have been added
        const currentVideoCount = story.pages?.filter(p => p.video_url).length || 0;
        const newVideoCount = updatedStory.pages?.filter(p => p.video_url).length || 0;

        if (newVideoCount > currentVideoCount || updatedStory.video_status !== story.video_status) {
          setStory(updatedStory);

          // Stop polling if all videos are complete
          if (updatedStory.video_status === 'complete' || updatedStory.video_status === 'failed') {
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Failed to poll for video updates:', error);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [storyId, story?.video_status]);

  // Connect to SSE for streaming page updates
  // TODO: Add automatic reconnection logic if SSE connection drops
  // TODO: Add "Retry Remaining Pages" button for partially failed stories
  useEffect(() => {
    if (!isStreaming || !storyId) return;

    const streamUrl = `${API}/v1/stories/${storyId}/stream`;
    const eventSource = new EventSource(streamUrl, { withCredentials: true });
    storyStreamRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'page_ready':
            // Update the story with the new page image
            setStory(prev => {
              if (!prev || !prev.pages) return prev;
              const updatedPages = [...prev.pages];
              if (data.page_index < updatedPages.length) {
                updatedPages[data.page_index] = {
                  ...updatedPages[data.page_index],
                  image_url: data.image_url
                };
              }
              return { ...prev, pages: updatedPages };
            });
            setStreamingProgress(prev => ({
              ...prev,
              readyPages: data.ready_pages,
              totalPages: data.total_pages,
            }));
            break;

          case 'audio_ready':
            // Update the story with the new audio
            setStory(prev => {
              if (!prev || !prev.pages) return prev;
              const updatedPages = [...prev.pages];
              if (data.page_index < updatedPages.length) {
                updatedPages[data.page_index] = {
                  ...updatedPages[data.page_index],
                  audio_url: data.audio_url
                };
              }
              return { ...prev, pages: updatedPages };
            });
            setStreamingProgress(prev => ({
              ...prev,
              pagesWithAudio: data.pages_with_audio,
            }));
            break;

          case 'generation_complete':
            setIsStreaming(false);
            eventSource.close();
            toast.success("Story is fully ready!");
            // Remove streaming param from URL
            navigate(`/story/${storyId}`, { replace: true });
            break;

          case 'generation_failed':
            setIsStreaming(false);
            eventSource.close();
            toast.error(`Story generation encountered an issue: ${data.error}`);
            break;

          case 'error':
            eventSource.close();
            break;
        }
      } catch (err) {
        console.error('Failed to parse SSE message:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('Story stream error:', err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
      storyStreamRef.current = null;
    };
  }, [isStreaming, storyId, navigate]);

  // Force layout recalculation after story loads to fix text overlay rendering
  useEffect(() => {
    if (!loading && story) {
      // Trigger resize event after a short delay to force browser layout recalculation
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      // Also trigger after a longer delay for slower renders
      const timer2 = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 500);
      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
      };
    }
  }, [loading, story]);

  // Also trigger resize on page change to fix text overlay on new pages
  useEffect(() => {
    if (story) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  const loadStory = async () => {
    let currentUser = null;

    try {
      // Check auth
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        currentUser = userRes.data;
        setUser(currentUser);
      } catch (error) {
        setUser(null);
      }

      // Load story from API
      const response = await axios.get(`${API}/v1/stories/${storyId}`, {
        withCredentials: true
      });
      const storyData = response.data;
      setStory(storyData);
      setIsFavorited(storyData.is_favorited || false);

      // Check if story is still generating (progressive viewing mode)
      const genStatus = storyData.generation_status;
      if (genStatus && genStatus.status === 'generating') {
        setIsStreaming(true);
        setStreamingProgress({
          readyPages: genStatus.ready_pages || 0,
          pagesWithAudio: genStatus.pages_with_audio || 0,
          totalPages: genStatus.total_pages || storyData.pages?.length || 0,
        });
      } else if (isStreamingMode && (!genStatus || genStatus.status === 'completed')) {
        // Story finished generating, update URL to remove streaming param
        navigate(`/story/${storyId}`, { replace: true });
      }

      // Save as last read story for "Continue Reading" feature (user-specific)
      if (currentUser?.user_id) {
        localStorage.setItem(`lastReadStory_${currentUser.user_id}`, JSON.stringify({
          id: response.data.id,
          title: response.data.title,
          thumbnail: response.data.thumbnail
        }));
      }

      // Increment view count
      await axios.post(`${API}/stories/${storyId}/view`);

    } catch (error) {
      console.error('Failed to load story:', error);

      // Clear stale lastReadStory if this story doesn't exist
      if (currentUser?.user_id) {
        const lastRead = localStorage.getItem(`lastReadStory_${currentUser.user_id}`);
        if (lastRead) {
          try {
            const parsed = JSON.parse(lastRead);
            if (parsed.id === storyId) {
              localStorage.removeItem(`lastReadStory_${currentUser.user_id}`);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      }

      toast.error("Story not found!");
      navigate('/explore');
    } finally {
      setLoading(false);
    }
  };

  // Poll for video generation status when videos are pending/generating
  useEffect(() => {
    const videoStatus = story?.video_status;

    // Only poll if videos are being generated
    if (!videoStatus || videoStatus === 'none' || videoStatus === 'complete' || videoStatus === 'failed') {
      return;
    }

    // Poll every 10 seconds to check if videos are ready
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/v1/stories/${storyId}`, {
          withCredentials: true
        });
        const updatedStory = response.data;

        // Check if video status changed
        if (updatedStory.video_status !== videoStatus) {
          setStory(updatedStory);

          if (updatedStory.video_status === 'complete') {
            toast.success("Video animations are ready!", {
              icon: "🎬",
              duration: 4000,
            });
          } else if (updatedStory.video_status === 'failed') {
            toast.error("Video generation failed", {
              duration: 4000,
            });
          }
        }
      } catch (error) {
        console.error('Failed to poll video status:', error);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [story?.video_status, storyId]);

  const handleFavorite = async () => {
    if (!user) {
      toast.error("Please login to favorite stories", {
        action: {
          label: "Login",
          onClick: () => navigate('/login')
        }
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/stories/${storyId}/favorite`, {}, {
        withCredentials: true
      });
      setIsFavorited(response.data.favorited);
      if (story) {
        setStory({
          ...story,
          favorites_count: response.data.favorites_count
        });
      }
      toast.success(response.data.favorited ? "Added to favorites!" : "Removed from favorites");
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: story?.title || 'Amazing Story',
          text: 'Check out this story on Tellor!',
          url
        });
      } catch (error) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleRegenerateImages = () => {
    if (regeneratingImages) return;
    setShowRegenerateModal(true);
  };

  const confirmRegenerateImages = async () => {
    setRegeneratingImages(true);
    setRegenerateProgress({ current: 0, total: story?.pages?.length || 0, message: 'Starting...' });

    // Use EventSource for SSE streaming
    const eventSource = new EventSource(
      `${API}/stories/${storyId}/regenerate-images`,
      { withCredentials: true }
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'start':
            setRegenerateProgress({
              current: 0,
              total: data.total,
              message: data.message
            });
            break;

          case 'progress':
            setRegenerateProgress({
              current: data.current,
              total: data.total,
              message: data.message
            });
            break;

          case 'complete':
            eventSource.close();
            setShowRegenerateModal(false);
            setRegeneratingImages(false);
            setRegenerateProgress({ current: 0, total: 0, message: '' });
            toast.success(data.message);
            // Reload the story to show new images
            loadStory();
            break;

          case 'error':
            eventSource.close();
            setShowRegenerateModal(false);
            setRegeneratingImages(false);
            setRegenerateProgress({ current: 0, total: 0, message: '' });
            toast.error(data.message);
            break;
        }
      } catch (e) {
        console.error('Error parsing SSE data:', e);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE error:', error);
      eventSource.close();
      setShowRegenerateModal(false);
      setRegeneratingImages(false);
      setRegenerateProgress({ current: 0, total: 0, message: '' });
      toast.error('Connection lost. Please try again.');
    };
  };

  const handleDownloadPdf = async () => {
    if (downloadingPdf) return;
    setShowOptionsMenu(false);
    setDownloadingPdf(true);
    toast.info("Generating PDF...", { duration: 3000 });

    try {
      const response = await axios.get(
        `${API}/stories/${storyId}/download-pdf`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${story.title || 'story'}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      if (error.response?.status === 403) {
        toast.error("You can only download your own stories");
      } else {
        toast.error("Failed to download PDF");
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleEditTitle = () => {
    setEditedTitle(story.title);
    setIsEditingTitle(true);
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle("");
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    if (editedTitle.trim() === story.title) {
      setIsEditingTitle(false);
      return;
    }

    setSavingTitle(true);
    try {
      await axios.patch(`${API}/stories/${storyId}`, {
        title: editedTitle.trim()
      }, {
        withCredentials: true
      });

      setStory({ ...story, title: editedTitle.trim() });
      setIsEditingTitle(false);
      toast.success("Title updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update title");
    } finally {
      setSavingTitle(false);
    }
  };

  // Use shared category labels for editing
  const categoryOptions = CATEGORY_LABELS;

  // Save story details (title or category) from modal
  const handleSaveDetails = async (field) => {
    if (savingDetails) return;

    const value = field === 'title' ? editedDetailsTitle : editedDetailsCategory;

    if (field === 'title' && !value.trim()) {
      toast.error("Title cannot be empty");
      return;
    }

    if (field === 'title' && value.trim() === story.title) {
      setEditingDetailsField(null);
      return;
    }

    if (field === 'category' && value === story.category) {
      setEditingDetailsField(null);
      return;
    }

    setSavingDetails(true);
    try {
      const updateData = field === 'title'
        ? { title: value.trim() }
        : { category: value };

      await axios.patch(`${API}/stories/${storyId}`, updateData, {
        withCredentials: true
      });

      setStory({
        ...story,
        ...(field === 'title' ? { title: value.trim() } : { category: value })
      });
      setEditingDetailsField(null);
      toast.success(`${field === 'title' ? 'Title' : 'Category'} updated!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update ${field}`);
    } finally {
      setSavingDetails(false);
    }
  };

  // Start editing a field in the details modal
  const startEditingDetails = (field) => {
    if (field === 'title') {
      setEditedDetailsTitle(story.title || '');
    } else if (field === 'category') {
      setEditedDetailsCategory(story.category || '');
    }
    setEditingDetailsField(field);
  };

  const isAuthor = user && story && user.user_id === story.user_id;

  // Navigation methods using pageFlip API (supports both 2D and 3D book)
  const flipNext = () => {
    // Check if next page is ready during streaming
    if (isStreaming && story?.pages) {
      const nextFlipbookPage = currentPage + (isMobileView ? 1 : 2);
      const nextStoryIndex = getStoryPageIndex(nextFlipbookPage);
      // Only check for story pages (not cover or "The End")
      if (nextStoryIndex >= 0 && nextStoryIndex < story.pages.length) {
        const nextPage = story.pages[nextStoryIndex];
        if (!nextPage?.image_url) {
          toast.info("This page is still being created...");
          return;
        }
      }
    }

    // 3D Book navigation
    if (use3DBook && book3DRef.current) {
      book3DRef.current.flipNext();
      return;
    }

    // 2D HTMLFlipBook navigation
    if (bookRef.current && bookRef.current.pageFlip) {
      try {
        const pageFlip = bookRef.current.pageFlip();
        const state = pageFlip.getState();

        // Don't flip if already flipping
        if (state === 'flipping') {
          return;
        }

        // Use animated flip
        pageFlip.flipNext('bottom');
      } catch (e) {
        console.error('flipNext error:', e);
      }
    }
  };

  const flipPrev = () => {
    // 3D Book navigation
    if (use3DBook && book3DRef.current) {
      book3DRef.current.flipPrev();
      return;
    }

    // 2D HTMLFlipBook navigation
    if (bookRef.current && bookRef.current.pageFlip) {
      try {
        const pageFlip = bookRef.current.pageFlip();
        const state = pageFlip.getState();

        // Don't flip if already flipping
        if (state === 'flipping') {
          return;
        }

        // Standard desktop view: use animated flip
        // Mobile/fullscreen: use instant turn (flipPrev causes issues in these modes)
        if (!isMobileView && !isFullscreen) {
          pageFlip.flipPrev('top');
        } else {
          pageFlip.turnToPrevPage();
        }
      } catch (e) {
        console.error('flipPrev error:', e);
      }
    }
  };

  // Check if a page is viewable (has image or not in streaming mode)
  const canGoToPage = (pageIndex) => {
    if (!isStreaming || !story?.pages) return true;
    const page = story.pages[pageIndex];
    return page?.image_url != null;
  };

  // Go to a specific page (for pagination dots and "Read Again")
  const goToPage = (pageIndex) => {
    // Prevent navigation to pages without images during streaming
    if (!canGoToPage(pageIndex)) {
      toast.info("This page is still being created...");
      return;
    }

    // Stop any audio that might be playing
    stopReading();

    // 3D Book navigation
    if (use3DBook && book3DRef.current) {
      book3DRef.current.turnToPage(pageIndex);
      return;
    }

    // 2D HTMLFlipBook navigation
    if (bookRef.current && bookRef.current.pageFlip) {
      try {
        const pageFlip = bookRef.current.pageFlip();
        const state = pageFlip.getState();

        // Don't navigate if already flipping
        if (state === 'flipping') {
          return;
        }

        // Use turnToPage for instant navigation
        pageFlip.turnToPage(pageIndex);
      } catch (e) {
        console.error('goToPage error:', e);
      }
    }
  };

  const handleFlip = (e) => {
    const newPage = e.data;
    setCurrentPage(newPage);

    // Save reading progress to localStorage
    if (story && user) {
      const progress = {
        id: storyId,
        title: story.title,
        thumbnail: story.thumbnail,
        page: newPage,
        timestamp: Date.now()
      };
      localStorage.setItem(`lastReadStory_${user.user_id}`, JSON.stringify(progress));
      localStorage.setItem(`readingProgress_${storyId}`, JSON.stringify({ page: newPage }));
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Sparkles className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'Quicksand, sans-serif' }}>Loading story...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!story) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--st-border-medium)' }} />
            <p className="font-bold mb-2 text-lg" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>Story not found</p>
            <button
              onClick={() => navigate('/explore')}
              className="px-5 py-2.5 rounded-lg font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
            >
              Explore Stories
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const storyPageCount = story.pages.length;
  // Total pages in flipbook:
  // Desktop: 1 blank (front) + 1 cover + N story pages + 1 blank (if odd pages) + 1 The End + 1 Back Cover
  // Mobile: 1 cover + N story pages + 1 The End + 1 Back Cover
  const extraBlankBeforeEnd = isMobileView ? 0 : (storyPageCount % 2 === 1 ? 1 : 0);
  const totalPages = isMobileView
    ? 1 + storyPageCount + 1 + 1 // cover + story + end + back cover
    : 2 + storyPageCount + extraBlankBeforeEnd + 2; // blank+cover + story + (maybe blank) + end+back cover
  // Check if we're on the last page/spread
  // Mobile: single page view, last when currentPage + 1 >= totalPages
  // Desktop: 2-page spread, last when currentPage + 2 >= totalPages
  const isLastSpread = isMobileView
    ? currentPage + 1 >= totalPages
    : currentPage + 2 >= totalPages;

  return (
    <PageLayout hideInFullscreen={isFullscreen}>
      <div
        ref={fullscreenRef}
        className={`story-viewer-container ${isFullscreen ? 'fullscreen-mode' : 'flex-1 overflow-hidden h-full'}`}
      >
        <div className={`max-w-7xl mx-auto px-2 py-2 md:py-2 md:px-3 flex flex-col h-full ${isFullscreen ? 'fullscreen-content' : ''}`}>
          {/* Compact Story Header - Hidden in fullscreen mode */}
          {!isFullscreen && (
          <div
            className="rounded-2xl px-3 py-2 md:px-4 md:py-2.5 mb-3 shadow-sm story-viewer-header flex-shrink-0"
          >
            <div className="flex items-center justify-between gap-2">
              {/* Left: Back + Title */}
              <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
                <button
                  data-testid="back-btn"
                  onClick={() => {
                    // Check if there's history within the app (React Router stores idx in state)
                    if (window.history.state && window.history.state.idx > 0) {
                      navigate(-1);
                    } else {
                      // No history (e.g., direct link), go to home
                      navigate('/home');
                    }
                  }}
                  className="flex-shrink-0 p-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>

                <div className="min-w-0 flex-1">
                  {isEditingTitle ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        className="story-title-input text-base md:text-lg font-bold px-3 py-1.5 rounded-lg w-full"
                        style={{ fontFamily: 'Chewy, cursive', maxWidth: '280px' }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveTitle();
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                        disabled={savingTitle}
                      />
                      <button onClick={handleSaveTitle} disabled={savingTitle} className="p-1 rounded-full bg-white/20 hover:bg-white/30">
                        <Check className="w-4 h-4 text-white" />
                      </button>
                      <button onClick={handleCancelEdit} disabled={savingTitle} className="p-1 rounded-full bg-white/20 hover:bg-white/30">
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="group flex items-center gap-2">
                      <h1 className="text-sm md:text-lg font-bold text-white truncate" style={{ fontFamily: 'Chewy, cursive' }}>
                        {story.series_title ? `${story.series_title}: ${story.title}` : story.title}
                      </h1>
                      <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
                      {/* Video generating indicator */}
                      {(story.video_status === 'pending' || story.video_status === 'generating') && (
                        <div
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: 'rgba(147, 51, 234, 0.3)', color: '#E9D5FF' }}
                          title="Videos are being generated in the background"
                        >
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span className="hidden sm:inline">Videos generating...</span>
                        </div>
                      )}
                      {isAuthor && (
                        <button
                          onClick={handleEditTitle}
                          className="hidden md:flex flex-shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-100 bg-white/20 hover:bg-white/30 transition-all"
                        >
                          <Pencil className="w-3 h-3 text-white" />
                        </button>
                      )}
                    </div>
                  )}
                  {/* Metadata - Hidden on mobile */}
                  <div className="hidden md:flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-white/80" style={{ fontFamily: 'Quicksand, sans-serif' }}>by</span>
                    <ClickableAuthor
                      userId={story.user_id}
                      name={story.author_name}
                      showAvatar={false}
                      size="small"
                      textStyle={{ color: 'rgba(255, 255, 255, 0.8)' }}
                    />
                    <span className="text-white/40">•</span>
                    <span className="text-xs text-white/80">{story.category}</span>
                    <span className="text-white/40">•</span>
                    <span className="text-xs text-white/80">{getBookTypeLabel(story.age_range)}</span>
                  </div>
                </div>
              </div>

              {/* Right: Actions - Clean and minimal */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Like Button */}
                <button
                  data-testid="favorite-btn"
                  onClick={handleFavorite}
                  className="h-8 px-2 rounded-lg bg-white/15 hover:bg-white/25 transition-colors flex items-center justify-center gap-1"
                  title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart className="w-4 h-4" fill={isFavorited ? 'white' : 'none'} stroke="white" />
                  <span className="text-xs font-semibold text-white">{story?.favorites_count || 0}</span>
                </button>

                {/* Views */}
                <div className="hidden md:flex h-8 px-2 items-center gap-1 rounded-lg bg-white/15" title="Views">
                  <Eye className="w-4 h-4 text-white" />
                  <span className="text-xs font-semibold text-white">{story?.views_count || 0}</span>
                </div>

                {/* Fullscreen Button */}
                <button
                  data-testid="fullscreen-btn"
                  onClick={handleToggleFullscreen}
                  className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 transition-all hover:scale-105 flex items-center justify-center"
                  title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-4 h-4 text-white" />
                  ) : (
                    <Maximize2 className="w-4 h-4 text-white" />
                  )}
                </button>

                {/* 3D Book Button - only show on tablets/desktops with WebGL */}
                {is3DBookAvailable && (
                  <button
                    onClick={handleToggle3DBook}
                    className="h-8 px-3 rounded-full bg-white/20 hover:bg-white/30 transition-all hover:scale-105 flex items-center justify-center gap-1.5"
                    title={use3DBook ? "Exit 3D book" : "Read in 3D"}
                  >
                    {use3DBook ? (
                      <>
                        <X className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Quicksand, sans-serif' }}>Exit 3D</span>
                      </>
                    ) : (
                      <>
                        <BookOpen className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-semibold text-white" style={{ fontFamily: 'Quicksand, sans-serif' }}>3D Book</span>
                      </>
                    )}
                  </button>
                )}

                {/* More Options Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                    className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 transition-colors flex items-center justify-center"
                    title="More options"
                  >
                    <MoreVertical className="w-4 h-4 text-white" />
                  </button>

                  {/* Dropdown Menu */}
                  {showOptionsMenu && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowOptionsMenu(false)}
                      />
                      {/* Menu */}
                      <div
                        className="absolute right-0 top-full mt-2 z-50 rounded-lg shadow-lg py-1"
                        style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}
                      >
                        {/* Share */}
                        <button
                          onClick={() => { handleShare(); setShowOptionsMenu(false); }}
                          className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-orange-50 dark:hover:bg-white/10 transition-colors text-sm"
                        >
                          <Share2 className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                          <span style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>Share</span>
                        </button>

                        {/* Story Details */}
                        <button
                          onClick={() => { setShowStoryDetails(true); setShowOptionsMenu(false); }}
                          className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-orange-50 dark:hover:bg-white/10 transition-colors text-sm"
                        >
                          <Info className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                          <span style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>Details</span>
                        </button>

                        {/* Order Print Copy - Coming Soon */}
                        <button
                          disabled
                          className="w-full px-3 py-2 text-left flex items-center gap-2.5 text-sm opacity-50 cursor-not-allowed whitespace-nowrap"
                        >
                          <Printer className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
                          <span style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>Print</span>
                          <span
                            className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0"
                            className="bg-orange-100 dark:bg-orange-900/30" style={{ color: 'var(--st-primary)', fontFamily: 'Quicksand, sans-serif' }}
                          >
                            Soon
                          </span>
                        </button>

                        {/* Author-only options */}
                        {isAuthor && (
                          <>
                            <div className="my-1 border-t" style={{ borderColor: 'var(--st-border-light)' }} />

                            {/* Download PDF */}
                            <button
                              onClick={handleDownloadPdf}
                              disabled={downloadingPdf}
                              className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-orange-50 dark:hover:bg-white/10 transition-colors text-sm disabled:opacity-50"
                            >
                              {downloadingPdf ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--st-primary)' }} />
                              ) : (
                                <Download className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                              )}
                              <span style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>Download PDF</span>
                            </button>

                            {/* Premium-only: Regenerate Thumbnail */}
                            {user?.is_premium && (
                              <button
                                onClick={() => { setShowThumbnailPicker(true); setShowOptionsMenu(false); }}
                                className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-orange-50 dark:hover:bg-white/10 transition-colors text-sm"
                              >
                                <Image className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                                <span style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>Change Thumbnail</span>
                              </button>
                            )}

                            {/* Premium-only: Storyboard Editor */}
                            {user?.is_premium && (
                              <button
                                onClick={() => { setShowStoryboard(true); setShowOptionsMenu(false); }}
                                className="w-full px-3 py-2 text-left flex items-center gap-2.5 hover:bg-orange-50 dark:hover:bg-white/10 transition-colors text-sm"
                              >
                                <Layers className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                                <span style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>Storyboard Editor</span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Streaming Progress Indicator - Show during progressive story viewing */}
          {isStreaming && (
            <div
              className="mb-4 px-4 py-3 rounded-xl flex items-center gap-3"
              style={{
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)',
                border: '1px solid rgba(34, 197, 94, 0.3)'
              }}
            >
              <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" style={{ color: '#16A34A' }} />
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: '#16A34A' }}>
                  Story is generating...
                </p>
                <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: '#22C55E' }}>
                  {streamingProgress.readyPages} of {streamingProgress.totalPages} pages ready
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${streamingProgress.totalPages > 0 ? (streamingProgress.readyPages / streamingProgress.totalPages) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, #22C55E 0%, #16A34A 100%)'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Fullscreen Exit Button - Top center in dark area on mobile, top right on desktop */}
          {isFullscreen && (
            <button
              onClick={handleToggleFullscreen}
              className="fixed top-3 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:top-4 md:right-4 z-50 p-2 md:p-3 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all hover:scale-110"
              title="Exit fullscreen (ESC)"
            >
              <Minimize2 className="w-4 h-4 md:w-5 md:h-5" style={{ color: 'var(--st-primary)' }} />
            </button>
          )}

          {/* Flipbook Container */}
          {use3DBook ? (
            <Suspense fallback={
              <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'linear-gradient(180deg, #1a1510 0%, #2d241c 100%)' }}>
                <div className="text-center">
                  <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" style={{ color: 'var(--st-primary)' }} />
                  <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-bg-page)', fontSize: '1.2rem' }}>Opening your storybook...</p>
                </div>
              </div>
            }>
              <div className="fixed inset-0 z-50" style={{ background: '#1a1510' }}>
                <Book3D
                  ref={book3DRef}
                  pages={story.pages}
                  coverImage={getImageUrl(story.thumbnail || story.book_cover || story.cover_image)}
                  title={story.title}
                  authorName={story.author_name}
                  currentPage={currentPage}
                  onPageChange={(page) => {
                    setCurrentPage(page);
                    // Save reading progress
                    localStorage.setItem(`readingProgress_${storyId}`, JSON.stringify({
                      page,
                      timestamp: Date.now()
                    }));
                  }}
                  // Audio props - only show Auto Read for v2+ stories (v1 has no audio)
                  isReading={isReadingMode}
                  isSpeaking={isSpeaking}
                  isTTSLoading={isLoadingAudio}
                  onToggleReading={story.version !== 1 ? toggleReadingMode : undefined}
                  // Streaming mode props
                  isStreaming={isStreaming}
                  onStreamingBlocked={() => toast.info("This page is still being created...")}
                />
                {/* Exit button for 3D book mode */}
                <button
                  onClick={handleToggle3DBook}
                  className="fixed top-4 right-4 z-50 p-3 rounded-full transition-all hover:scale-110"
                  style={{
                    background: 'rgba(255, 248, 240, 0.15)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 248, 240, 0.2)'
                  }}
                  title="Exit 3D book"
                >
                  <X className="w-5 h-5" style={{ color: 'var(--st-bg-page)' }} />
                </button>
                {/* Back button */}
                <button
                  onClick={() => navigate(-1)}
                  className="fixed top-4 left-4 z-50 p-3 rounded-full transition-all hover:scale-110"
                  style={{
                    background: 'rgba(255, 248, 240, 0.15)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 248, 240, 0.2)'
                  }}
                  title="Go back"
                >
                  <ChevronLeft className="w-5 h-5" style={{ color: 'var(--st-bg-page)' }} />
                </button>
              </div>
            </Suspense>
          ) : (
          <div className={`flipbook-container ${isFullscreen ? '' : 'flex-1'} min-h-0 mb-2 ${currentPage <= 1 && !isMobileView ? 'cover-view' : ''} ${isLastSpread && !isMobileView ? 'end-view' : ''}`}>
            <HTMLFlipBook
              key={`book-${Math.round(bookDimensions.width / 50) * 50}-${Math.round(bookDimensions.height / 50) * 50}-${isMobileView ? 'mobile' : 'desktop'}-${isFullscreen ? 'fs' : 'std'}`}
              ref={bookRef}
              width={bookDimensions.width}
              height={bookDimensions.height}
              size="fixed"
              minWidth={isMobileView ? 280 : 260}
              maxWidth={isMobileView ? 450 : 700}
              minHeight={isMobileView ? 400 : 350}
              maxHeight={isMobileView ? 650 : 900}
              startPage={currentPage}
              flippingTime={800}
              drawShadow={true}
              maxShadowOpacity={0.5}
              showCover={false}
              usePortrait={isMobileView}
              mobileScrollSupport={false}
              disableFlipByClick={true}
              clickEventForward={false}
              useMouseEvents={false}
              onFlip={handleFlip}
                            className="storybook-flipbook"
            >
              {/* Traditional book layout:
                  Desktop:
                  - Spread 0: BlankPage (left) | FrontCover (right) - CSS hides left page
                  - Spread 1+: Story pages in pairs [Page1|Page2], [Page3|Page4], etc.
                  - Final: TheEndPage on the left side with blank on right

                  Mobile (single-page view):
                  - Just Cover, then story pages, then The End (no blank pages needed)

                  Note: We build pages as an array to avoid conditional rendering returning false,
                  which HTMLFlipBook cannot handle.
              */}
              {[
                // Blank page (inside front cover - left side of first spread) - DESKTOP ONLY
                ...(!isMobileView ? [<BlankPage key="blank-front" />] : []),

                // Front Cover (right side of first spread on desktop, first page on mobile)
                <FrontCoverPage
                  key="front-cover"
                  story={story}
                  isFullscreen={isFullscreen}
                  isStreaming={isStreaming}
                />,

                // Story pages
                ...story.pages.map((page, idx) => (
                  <PageSpread
                    key={`page-${idx}`}
                    page={page}
                    previousPage={idx > 0 ? story.pages[idx - 1] : null}
                    pageNumber={page.page_number}
                    isFullscreen={isFullscreen}
                  />
                )),

                // Add blank page before The End if story has ODD number of pages - DESKTOP ONLY
                // This ensures The End appears on the LEFT side of its spread (even index)
                ...(!isMobileView && story.pages.length % 2 === 1 ? [<BlankPage key="blank-before-end" />] : []),

                // The End Page
                <TheEndPage
                  key="the-end"
                  story={story}
                  pageNumber={storyPageCount + 1}
                  isFullscreen={isFullscreen}
                  onCreateStory={() => navigate('/create')}
                  onExplore={() => navigate('/explore')}
                  onReadAgain={() => goToPage(0)}
                />,

                // Back cover page - Both desktop and mobile
                <BackCoverPage key="back-cover" story={story} isFullscreen={isFullscreen} />,
              ]}
            </HTMLFlipBook>
          </div>
          )}

          {/* Read to Me Button Container - Fixed height to prevent layout shift */}
          <div className="h-10 mt-4 mb-2 flex items-center justify-center flex-shrink-0">
            {/* Only show button on story pages with audio (story pages start at index 2) */}
            {currentPage >= FRONT_COVER_OFFSET && currentPage < FRONT_COVER_OFFSET + storyPageCount && storyHasAudio && (
              <div className="flex items-center">
              {user?.is_premium ? (
                <>
                  <button
                    onClick={toggleReadingMode}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all text-sm shadow-sm ${
                      isReadingMode
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                        : 'text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10'
                    }`}
                    style={{ fontFamily: 'Quicksand, sans-serif', border: isReadingMode ? 'none' : '1px solid #E9D5FF', background: isReadingMode ? undefined : 'var(--st-bg-surface)' }}
                  >
                    {isReadingMode ? (
                      <>
                        <VolumeX className="w-4 h-4" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-4 h-4" />
                        Auto Read
                      </>
                    )}
                  </button>

                  {/* Speaking indicator */}
                  {isSpeaking && (
                    <div className="ml-3 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" />
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => {
                    toast.error("Auto Read is a premium feature", {
                      description: "Upgrade to premium to have stories read aloud",
                      action: {
                        label: "Upgrade",
                        onClick: () => navigate('/profile')
                      }
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all text-sm bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed shadow-sm"
                  style={{ fontFamily: 'Quicksand, sans-serif', border: '1px solid #E5E7EB' }}
                >
                  <Volume2 className="w-4 h-4" />
                  Auto Read
                  <Crown className="w-3.5 h-3.5 text-amber-500" />
                </button>
              )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flipbook-nav flex-shrink-0">
            <button
              data-testid="prev-page-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (currentPage > 0) {
                  stopReading();
                  flipPrev();
                }
              }}
              disabled={currentPage === 0}
              className="prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>

            {/* Progress Indicator */}
            <div className="page-indicator" data-testid="page-counter">
              <p className="page-text">
                {/* Show contextual page info based on current position
                    Layout: [Blank|Cover] at 0-1, story pages start at index 2
                */}
                {(() => {
                  // Cover spread (blank + cover at indices 0-1)
                  if (currentPage <= 1) return 'Cover';

                  // Story page indices start at 2
                  const storyPageIdx = currentPage - FRONT_COVER_OFFSET; // FRONT_COVER_OFFSET = 2

                  // Check if we're past all story pages (The End section)
                  if (storyPageIdx >= storyPageCount) return 'The End';

                  // On story pages
                  if (isMobileView) {
                    // Mobile: single page view
                    return `Page ${storyPageIdx + 1} of ${storyPageCount}`;
                  } else {
                    // Desktop: two-page spread
                    const leftPageNum = storyPageIdx + 1;
                    const rightPageNum = storyPageIdx + 2;
                    if (rightPageNum <= storyPageCount) {
                      return `Pages ${leftPageNum}-${rightPageNum} of ${storyPageCount}`;
                    } else {
                      return `Page ${leftPageNum} of ${storyPageCount}`;
                    }
                  }
                })()}
              </p>
              <div className="progress-dots">
                {/* Clickable dots - mobile: one per page, desktop: one per spread (pair) */}
                {(() => {
                  const dotCount = isMobileView ? totalPages : Math.ceil(totalPages / 2);
                  const activeIdx = isMobileView ? currentPage : Math.floor(currentPage / 2);

                  // For long stories (> 12 dots), show condensed pagination
                  const MAX_DOTS = 12;
                  if (dotCount > MAX_DOTS) {
                    const dots = [];
                    const halfVisible = Math.floor(MAX_DOTS / 2) - 1; // Reserve space for ellipsis

                    // Always show first dot (cover)
                    dots.push(
                      <button
                        key={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          stopReading();
                          goToPage(0);
                        }}
                        className={`progress-dot clickable ${activeIdx === 0 ? 'active' : ''}`}
                        aria-label="Go to cover"
                        title="Cover"
                      />
                    );

                    // Determine range around active page
                    let startIdx, endIdx;
                    if (activeIdx <= halfVisible + 1) {
                      // Near start
                      startIdx = 1;
                      endIdx = MAX_DOTS - 2;
                    } else if (activeIdx >= dotCount - halfVisible - 2) {
                      // Near end
                      startIdx = dotCount - MAX_DOTS + 2;
                      endIdx = dotCount - 1;
                    } else {
                      // Middle
                      startIdx = activeIdx - halfVisible;
                      endIdx = activeIdx + halfVisible;
                    }

                    // Show ellipsis if needed before middle section
                    if (startIdx > 1) {
                      dots.push(
                        <span
                          key="ellipsis-start"
                          className="progress-ellipsis"
                          style={{ padding: '0 2px', color: 'var(--st-text-muted)', fontSize: '12px' }}
                        >
                          ...
                        </span>
                      );
                    }

                    // Show middle dots
                    for (let idx = startIdx; idx <= endIdx; idx++) {
                      const targetPage = isMobileView ? idx : idx * 2;
                      dots.push(
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            stopReading();
                            goToPage(targetPage);
                          }}
                          className={`progress-dot clickable ${activeIdx === idx ? 'active' : ''}`}
                          aria-label={`Go to page ${targetPage + 1}`}
                          title={`Page ${targetPage + 1}`}
                        />
                      );
                    }

                    // Show ellipsis if needed after middle section
                    if (endIdx < dotCount - 1) {
                      dots.push(
                        <span
                          key="ellipsis-end"
                          className="progress-ellipsis"
                          style={{ padding: '0 2px', color: 'var(--st-text-muted)', fontSize: '12px' }}
                        >
                          ...
                        </span>
                      );
                    }

                    return dots;
                  }

                  // For short stories, show all dots as before
                  return Array.from({ length: dotCount }).map((_, idx) => {
                    const isActive = activeIdx === idx;
                    const targetPage = isMobileView ? idx : idx * 2;

                    return (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          stopReading();
                          goToPage(targetPage);
                        }}
                        className={`progress-dot clickable ${isActive ? 'active' : ''}`}
                        aria-label={idx === 0 ? 'Go to cover' : idx === dotCount - 1 ? 'Go to end' : `Go to page ${targetPage + 1}`}
                        title={idx === 0 ? 'Cover' : idx === dotCount - 1 ? 'The End' : `Page ${targetPage + 1}`}
                      />
                    );
                  });
                })()}
              </div>
            </div>

            <button
              data-testid="next-page-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isLastSpread) {
                  stopReading();
                  flipNext();
                }
              }}
              disabled={isLastSpread}
              className={`next-btn ${readingComplete && isReadingMode && !isLastSpread ? 'pulse-glow' : ''}`}
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Hint */}
          <div className="text-center text-xs text-gray-400 dark:text-gray-500 mb-1 flex-shrink-0" style={{ fontFamily: 'Quicksand, sans-serif' }}>
            Use arrow keys, buttons, or click dots to navigate
          </div>
        </div>
      </div>

      {/* Regenerate Images Confirmation Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !regeneratingImages && setShowRegenerateModal(false)}
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl p-6 max-w-sm mx-4 shadow-xl"
            style={{
              background: 'var(--st-bg-surface)',
              border: '1px solid var(--st-border-light)'
            }}
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
              >
                {regeneratingImages ? (
                  <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--st-primary)' }} />
                ) : (
                  <RefreshCw className="w-7 h-7" style={{ color: 'var(--st-primary)' }} />
                )}
              </div>
            </div>

            {/* Title */}
            <h3
              className="text-xl font-bold text-center mb-2"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              {regeneratingImages ? 'Regenerating Images...' : 'Regenerate Images?'}
            </h3>

            {/* Description or Progress */}
            {regeneratingImages ? (
              <div className="mb-6">
                {/* Progress message */}
                <p
                  className="text-center mb-4"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                >
                  {regenerateProgress.message}
                </p>

                {/* Progress bar */}
                <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: 'var(--st-border-light)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: regenerateProgress.total > 0
                        ? `${(regenerateProgress.current / regenerateProgress.total) * 100}%`
                        : '0%',
                      background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)'
                    }}
                  />
                </div>

                {/* Progress count */}
                <p
                  className="text-center mt-2 text-sm"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                >
                  {regenerateProgress.current} of {regenerateProgress.total} images
                </p>
              </div>
            ) : (
              <p
                className="text-center mb-6"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                This will create new illustrations for all pages in your story. This may take a few minutes.
              </p>
            )}

            {/* Buttons */}
            {!regeneratingImages && (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowRegenerateModal(false)}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-sm"
                  style={{
                    background: 'white',
                    color: 'var(--st-text-heading)',
                    fontFamily: 'Quicksand, sans-serif',
                    border: '1px solid var(--st-border-light)'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRegenerateImages}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all"
                  style={{
                    background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                    color: 'white',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  Regenerate
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Story Details Modal */}
      {showStoryDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowStoryDetails(false)}
          />

          {/* Modal */}
          <div
            className="relative max-w-md w-full mx-4 shadow-2xl max-h-[85vh] overflow-hidden rounded-2xl"
            style={{ background: 'var(--st-bg-surface)' }}
          >
            {/* Header with Thumbnail */}
            <div className="relative">
              {/* Thumbnail Background */}
              <div className="h-32 overflow-hidden relative">
                {story?.thumbnail ? (
                  <img
                    src={getImageUrl(story.thumbnail)}
                    alt={story.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                  >
                    <Sparkles className="w-12 h-12" style={{ color: 'var(--st-primary)', opacity: 0.5 }} />
                  </div>
                )}
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-[var(--st-bg-surface)]" />
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowStoryDetails(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 dark:bg-black/50 backdrop-blur-sm hover:bg-white dark:hover:bg-black/70 transition-all shadow-md flex items-center justify-center"
                style={{ border: '1px solid rgba(255,140,97,0.3)' }}
              >
                <X className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
              </button>

              {/* Version Badge */}
              <div className="absolute top-3 left-3 flex items-center gap-1.5">
                <span className="shadow-lg rounded-md">
                  <VersionBadge version={story?.version} isProStory={story?.is_pro_story} />
                </span>
              </div>

              {/* Title Section - Overlapping */}
              <div className="px-5 -mt-6 relative z-10">
                <div className="rounded-xl p-4 shadow-lg" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {editingDetailsField === 'title' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editedDetailsTitle}
                            onChange={(e) => setEditedDetailsTitle(e.target.value)}
                            className="flex-1 text-lg font-bold px-3 py-1.5 rounded-lg border-2 focus:outline-none focus:border-orange-400"
                            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)', borderColor: 'var(--st-border-light)', background: 'var(--st-bg-surface)' }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveDetails('title');
                              if (e.key === 'Escape') setEditingDetailsField(null);
                            }}
                            disabled={savingDetails}
                          />
                          <button
                            onClick={() => handleSaveDetails('title')}
                            disabled={savingDetails}
                            className="p-1.5 rounded-full bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50 transition-colors"
                          >
                            {savingDetails ? (
                              <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                            ) : (
                              <Check className="w-4 h-4 text-green-600" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingDetailsField(null)}
                            disabled={savingDetails}
                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 transition-colors"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ) : (
                        <h2
                          className="text-xl font-bold truncate"
                          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                        >
                          {story?.title}
                        </h2>
                      )}
                      <p className="text-xs mt-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        {story?.pages?.length || 0} pages • {getBookTypeLabel(story?.age_range) || 'All ages'}
                      </p>
                    </div>
                    {isAuthor && editingDetailsField !== 'title' && (
                      <button
                        onClick={() => startEditingDetails('title')}
                        className="p-2 rounded-lg hover:bg-orange-50 dark:hover:bg-white/10 transition-colors flex-shrink-0"
                        title="Edit title"
                      >
                        <Pencil className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="px-5 pb-5 pt-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 180px)' }}>
              {/* Quick Stats Row */}
              <div className="grid grid-cols-2 gap-3">
                {/* Category - Editable by author */}
                <div
                  className="p-3 rounded-xl transition-all bg-orange-50/50 dark:bg-white/5"
                  style={{ border: '1px solid var(--st-border-light)' }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                      <span className="text-xs font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Category
                      </span>
                    </div>
                    {isAuthor && editingDetailsField !== 'category' && (
                      <button
                        onClick={() => startEditingDetails('category')}
                        className="p-1 rounded hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                      >
                        <Pencil className="w-3 h-3" style={{ color: 'var(--st-primary)' }} />
                      </button>
                    )}
                  </div>
                  {editingDetailsField === 'category' ? (
                    <div className="flex items-center gap-1">
                      <select
                        value={editedDetailsCategory}
                        onChange={(e) => setEditedDetailsCategory(e.target.value)}
                        className="flex-1 text-sm font-semibold px-2 py-1 rounded-lg border focus:outline-none focus:border-orange-400"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)', borderColor: 'var(--st-border-medium)', background: 'var(--st-bg-surface)' }}
                        disabled={savingDetails}
                      >
                        {categoryOptions.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleSaveDetails('category')}
                        disabled={savingDetails}
                        className="p-1 rounded bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50"
                      >
                        {savingDetails ? <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600" /> : <Check className="w-3.5 h-3.5 text-green-600" />}
                      </button>
                      <button onClick={() => setEditingDetailsField(null)} disabled={savingDetails} className="p-1 rounded bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50">
                        <X className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-bold capitalize" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      {story?.category || 'Not set'}
                    </p>
                  )}
                </div>

                {/* Art Style */}
                <div
                  className="p-3 rounded-xl bg-orange-50/50 dark:bg-white/5"
                  style={{ border: '1px solid var(--st-border-light)' }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Palette className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                    <span className="text-xs font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      Art Style
                    </span>
                  </div>
                  <p className="text-sm font-bold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    {getArtStyleLabel(story)}
                  </p>
                </div>
              </div>

              {/* Original Prompt */}
              {story?.prompt && (
                <div
                  className="p-3 rounded-xl bg-orange-50/50 dark:bg-white/5"
                  style={{ border: '1px solid var(--st-border-light)' }}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Wand2 className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                    <span className="text-xs font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      Original Prompt
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    "{story.prompt}"
                  </p>
                </div>
              )}

              {/* Characters */}
              {story?.structure?.characters && story.structure.characters.length > 0 && (
                <div
                  className="p-3 rounded-xl bg-orange-50/50 dark:bg-white/5"
                  style={{ border: '1px solid var(--st-border-light)' }}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Users className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                    <span className="text-xs font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      Characters
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {story.structure.characters.map((character, idx) => {
                      // Handle both string characters and object characters
                      if (typeof character === 'string') {
                        return (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--st-primary)' }} />
                            <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                              {character}
                            </span>
                          </div>
                        );
                      }
                      // Object with name/description
                      return (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--st-primary)' }} />
                          <div>
                            <span className="text-sm font-bold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}>
                              {character.name}
                            </span>
                            {character.description && (
                              <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                                {' — '}{character.description}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Theme & Moral */}
              {(story?.structure?.theme || story?.structure?.moral) && (
                <div
                  className="p-3 rounded-xl bg-orange-50/50 dark:bg-white/5"
                  style={{ border: '1px solid var(--st-border-light)' }}
                >
                  {story.structure.theme && (
                    <div className={story.structure.moral ? 'mb-2' : ''}>
                      <span className="text-xs font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Theme
                      </span>
                      <p className="text-sm mt-0.5" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                        {story.structure.theme}
                      </p>
                    </div>
                  )}
                  {story.structure.moral && (
                    <div>
                      <span className="text-xs font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Moral
                      </span>
                      <p className="text-sm italic mt-0.5" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                        "{story.structure.moral}"
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Created Date Footer */}
              {story?.created_at && (
                <div className="pt-2 flex items-center justify-center gap-2 text-xs" style={{ color: 'var(--st-text-muted)' }}>
                  <Calendar className="w-3 h-3" />
                  <span style={{ fontFamily: 'Quicksand, sans-serif' }}>
                    Created {new Date(story.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}

              {/* Admin-only API Cost Panel */}
              {user?.is_admin && story?.api_costs && (
                <div
                  className="mt-4 p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/50"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold">$</span>
                    </div>
                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-300" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                      API Costs (Admin Only)
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-xs">
                    <div className="text-center p-2 bg-white/60 dark:bg-white/10 rounded-lg">
                      <p className="font-bold text-purple-700 dark:text-purple-300">
                        ${story.api_costs.llm?.total?.toFixed(4) || '0.0000'}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400">LLM</p>
                    </div>
                    <div className="text-center p-2 bg-white/60 dark:bg-white/10 rounded-lg">
                      <p className="font-bold text-blue-700 dark:text-blue-300">
                        ${story.api_costs.images?.total?.toFixed(4) || '0.0000'}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400">Images</p>
                    </div>
                    <div className="text-center p-2 bg-white/60 dark:bg-white/10 rounded-lg">
                      <p className="font-bold text-green-700 dark:text-green-300">
                        ${story.api_costs.audio?.total?.toFixed(4) || '0.0000'}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400">Audio</p>
                    </div>
                    <div className="text-center p-2 bg-white/60 dark:bg-white/10 rounded-lg">
                      <p className="font-bold text-amber-700 dark:text-amber-300">
                        ${story.api_costs.videos?.total?.toFixed(4) || '0.0000'}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400">Video</p>
                    </div>
                    <div className="text-center p-2 bg-purple-100 dark:bg-purple-800/40 rounded-lg">
                      <p className="font-bold text-purple-800 dark:text-purple-200">
                        ${story.api_costs.total?.toFixed(4) || '0.0000'}
                      </p>
                      <p className="text-purple-600 dark:text-purple-300 font-medium">Total</p>
                    </div>
                  </div>
                  {story.api_costs.images && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
                      {story.api_costs.images.count || 0} images via {story.api_costs.images.provider || 'unknown'}
                      {story.api_costs.videos?.count > 0 && ` • ${story.api_costs.videos.count} videos`}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Storyboard Editor Overlay */}
      {showStoryboard && story && (
        <StoryboardEditor
          story={story}
          onClose={() => setShowStoryboard(false)}
          onPageUpdated={(pageNum, newImageUrl) => {
            setStory(prev => ({
              ...prev,
              pages: prev.pages.map((p, i) =>
                i === pageNum - 1 ? { ...p, image_url: newImageUrl } : p
              )
            }));
          }}
        />
      )}

      {/* Thumbnail Picker */}
      {showThumbnailPicker && story && (
        <ThumbnailPicker
          story={story}
          isOpen={showThumbnailPicker}
          onClose={() => setShowThumbnailPicker(false)}
          onThumbnailChange={(newThumbnail, thumbnails, activeIndex) => {
            setStory(prev => ({
              ...prev,
              thumbnail: newThumbnail,
              thumbnails: thumbnails,
              active_thumbnail_index: activeIndex,
              regeneration_count: Math.max(prev.regeneration_count || 0, thumbnails.length - 1)
            }));
          }}
        />
      )}
    </PageLayout>
  );
};

export default StoryViewer;
